import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { FileRulesType } from '@astronautsid/wpe-utils'

import { complimentaryDocList } from 'constant/vendor'
import { mapCreditLimitDocumentsForForm } from 'features/Enterprise/Vendor/helpers/vendorDocumentForm'
import {
  type VendorDetailsInterface,
  type VendorFormType,
  type VendorDocumentType,
  type VendorCompanyType,
  type RevisionInfoType,
  type VendorOptionsType,
  type VendorUomsType,
  type AstroPicType,
  type LocationType,
  type VendorDetailRequestInterface,
} from 'features/Enterprise/Vendor/types/VendorManagement'
import sidebarList from '../../constants/sidebarList'

export const SLICE_NAME = 'addEditVendor'

interface AddEditVendorStateInterface {
  isLoading: boolean
  vendorDetails: VendorDetailsInterface
  form: VendorFormType
  vendorCompanies: VendorCompanyType[]
  vendorDocuments: VendorDocumentType[]
  pksDocuments: VendorDocumentType[]
  revisionInfo: RevisionInfoType
  previewDialogData: {
    isShow: boolean
    idx?: number
    type?: string
    vendorDocs?: VendorDocumentType[]
  }
  pkpType: 'pkp' | 'non-pkp'
  vendorOptions: VendorOptionsType[]
  vendorUoms: VendorUomsType[]
  astroPicList: AstroPicType[]
  hubList: LocationType[]
  whList: LocationType[]
  fileRules: FileRulesType
  megahubList: LocationType[]
  sidebarItems: {
    label: string
    key: string
    description: string
    isSelected: boolean
  }[]
  shippingLocationList: LocationType[]
}

const initialState: AddEditVendorStateInterface = {
  isLoading: false,
  vendorDetails: {} as VendorDetailsInterface,
  form: {} as VendorFormType,
  revisionInfo: {} as RevisionInfoType,
  vendorCompanies: [],
  vendorDocuments: [],
  pksDocuments: [],
  previewDialogData: {
    isShow: false,
    idx: 0,
    type: '',
    vendorDocs: [],
  },
  pkpType: 'pkp',
  vendorOptions: [],
  vendorUoms: [],
  astroPicList: [],
  hubList: [],
  whList: [],
  fileRules: {
    extensions: ['.pdf', '.jpg', '.jpeg', '.png'],
    maxFileSize: 10000000,
    typeId: 'commercial-vendors-invoices',
  },
  megahubList: [],
  sidebarItems: sidebarList,
  shippingLocationList: [],
}

const addEditVendorSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setVendorDetails: (state, action: PayloadAction<VendorDetailsInterface>) => {
      state.vendorDetails = action.payload
    },
    setForm: (
      state,
      action: PayloadAction<{
        key: keyof VendorFormType
        value: unknown
      }>,
    ) => {
      const { key, value } = action.payload
      state.form[key] = value as never
    },
    setVendorCompany: (state, action: PayloadAction<VendorCompanyType[]>) => {
      state.vendorCompanies = action.payload
    },
    setVendorDocuments: (state, action: PayloadAction<VendorDocumentType[]>) => {
      state.vendorDocuments = action.payload
    },
    setVendorDetailRequest: (state, action: PayloadAction<VendorDetailRequestInterface>) => {
      const { request_data } = action.payload

      const pksDocs = request_data.documents?.filter((doc) => doc.document_type === 'pks') || []
      const nonPkpDocs =
        request_data.documents
          ?.filter((doc) => doc.document_type === 'sp_non_pkp')
          ?.map((doc) => ({
            ...doc,
            label: 'Surat Pernyataan Non-PKP',
          })) || []
      const nonPksDocs = complimentaryDocList(request_data.vendor_type).map((compDoc) => {
        const doc = request_data?.documents?.find((x) => x.document_type === compDoc.document_type)
        if (doc) {
          return {
            ...doc,
            label: compDoc.label,
            required: compDoc.required,
          }
        }

        return compDoc
      })
      const creditLimitDocs = mapCreditLimitDocumentsForForm(request_data.documents)

      state.form = {
        ...request_data,
        bank_accounts: request_data?.bank_accounts?.map((bank) => ({
          ...bank,
          form_id: window.crypto.randomUUID(),
        })),
        general_contacts: request_data?.general_contacts?.map((contact) => ({
          ...contact,
          form_id: window.crypto.randomUUID(),
        })),
        documents: [
          ...pksDocs,
          ...nonPkpDocs,
          ...nonPksDocs,
          ...creditLimitDocs,
        ] as VendorDocumentType[],
        state: request_data.locations[0]?.state || '',
        postal_code: request_data.locations[0]?.postal_code || '',
        address: request_data.locations[0]?.address || '',
        country: request_data.locations[0]?.country || '',
        address_label: request_data.locations[0]?.address_label || '',
        city: request_data.locations[0]?.city || '',
      }
      state.revisionInfo = action.payload
      state.vendorDetails = {
        ...request_data,
        documents: [
          ...pksDocs.filter((x) => x.file_path.length),
          ...nonPkpDocs.filter((x) => x.file_path.length),
          ...nonPksDocs.filter((x) => x.file_path.length),
          ...creditLimitDocs.filter((x) => x.file_path.length),
        ] as VendorDocumentType[],
      }
    },
    resetForm: (state) => {
      state.form = initialState.form
      state.revisionInfo = initialState.revisionInfo
      state.vendorDetails = initialState.vendorDetails
      state.sidebarItems = sidebarList.map((item) => ({ ...item }))
    },
    setPreviewDialogData: (
      state,
      action: PayloadAction<AddEditVendorStateInterface['previewDialogData']>,
    ) => {
      state.previewDialogData = action.payload
    },
    setPkpType: (state, action: PayloadAction<'pkp' | 'non-pkp'>) => {
      state.pkpType = action.payload
    },
    setVendorOptions: (state, action: PayloadAction<VendorOptionsType[]>) => {
      state.vendorOptions = action.payload
    },
    setVendorUoms: (state, action: PayloadAction<VendorUomsType[]>) => {
      state.vendorUoms = action.payload
    },
    setAstroPicList: (state, action: PayloadAction<AstroPicType[]>) => {
      state.astroPicList = action.payload
    },
    setHubLocationList: (state, action: PayloadAction<LocationType[]>) => {
      state.hubList = action.payload.filter((loc) => loc.location_active)
    },
    setWhLocationList: (state, action: PayloadAction<LocationType[]>) => {
      state.whList = action.payload.filter((loc) => loc.location_active)
    },
    setFileRules: (state, action: PayloadAction<FileRulesType>) => {
      state.fileRules = action.payload
    },
    setMegahubList: (state, action: PayloadAction<LocationType[]>) => {
      state.megahubList = action.payload.filter((loc) => loc.location_active)
    },
    setSidebarItems: (
      state,
      action: PayloadAction<AddEditVendorStateInterface['sidebarItems']>,
    ) => {
      state.sidebarItems = action.payload
    },
    setShippingLocationList: (state, action: PayloadAction<LocationType[]>) => {
      state.shippingLocationList = action.payload
    },
  },
})

export const {
  setLoading,
  setVendorDetails,
  setForm,
  setVendorCompany,
  setVendorDocuments,
  setVendorDetailRequest,
  resetForm,
  setPreviewDialogData,
  setPkpType,
  setVendorOptions,
  setVendorUoms,
  setAstroPicList,
  setHubLocationList,
  setWhLocationList,
  setFileRules,
  setMegahubList,
  setSidebarItems,
  setShippingLocationList,
} = addEditVendorSlice.actions

export default addEditVendorSlice.reducer
